import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Box, ButtonBase, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useCallback, useRef, useState } from 'react'

import { Flash, Gear } from '@/icons'

import {
  sidebarButtonRootSx,
  sidebarChevronSx,
  sidebarMenuAnchorOrigin,
  sidebarMenuSlotProps,
  sidebarMenuTransformOrigin,
} from '../sidebarButtonStyles'

import { useTexts } from './useTexts'

interface Props {
  disabled?: boolean
  testid?: string
  onInstantBuild: () => void
  onCustomBuild?: () => void
}

export function BuildRoutesButton(props: Props) {
  const { disabled = false, testid, onInstantBuild, onCustomBuild } = props
  const chevronRef = useRef<HTMLSpanElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const texts = useTexts()

  const handleToggle = useCallback(() => {
    if (disabled) return
    setAnchorEl(prev => (prev ? null : chevronRef.current))
  }, [disabled])

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleInstantBuild = useCallback(() => {
    setAnchorEl(null)
    onInstantBuild()
  }, [onInstantBuild])

  const handleCustomBuild = useCallback(() => {
    setAnchorEl(null)
    onCustomBuild?.()
  }, [onCustomBuild])

  return (
    <>
      <ButtonBase
        onClick={handleToggle}
        disabled={disabled}
        data-trackid={testid}
        data-testid={testid}
        sx={sidebarButtonRootSx}
      >
        <Stack direction="row" alignItems="center" width="100%">
          <Box
            minWidth={20}
            mr={2.25}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={disabled ? 'text.disabled' : 'text.primary'}
          >
            <Flash size={14} />
          </Box>
          <Typography
            variant="body1"
            fontWeight={500}
            color={disabled ? 'text.disabled' : 'text.primary'}
            align="left"
            noWrap
            flex={1}
          >
            {texts.buildRoutes}
          </Typography>
          {!disabled && (
            <IconButton
              ref={chevronRef}
              size="small"
              component="span"
              aria-expanded={open}
              sx={sidebarChevronSx}
            >
              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </IconButton>
          )}
        </Stack>
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={sidebarMenuAnchorOrigin}
        transformOrigin={sidebarMenuTransformOrigin}
        slotProps={sidebarMenuSlotProps}
      >
        <MenuItem
          onClick={handleInstantBuild}
          data-testid={`${testid}__instant-build`}
          data-trackid={`${testid}__instant-build`}
        >
          <Box mr={1.5} display="flex" alignItems="center" color="action.active">
            <Flash size={14} />
          </Box>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="body1" color="text.primary">
              {texts.instantBuild}
            </Typography>
            <Typography variant="caption" color="text.secondary" letterSpacing="0.4px">
              {texts.defaultLabel}
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={handleCustomBuild}
          data-testid={`${testid}__custom-build`}
          data-trackid={`${testid}__custom-build`}
        >
          <Box mr={1.5} display="flex" alignItems="center" color="action.active">
            <Gear size={14} />
          </Box>
          <Typography variant="body1" color="text.primary">
            {texts.customBuild}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  )
}
